import { z } from 'zod';
import { I18nStringSchema, ShortIdSchema, TimestampSchema, UidSchema } from './primitives.js';

export const AudienceSegmentSchema = z.object({
  id: ShortIdSchema,
  name: z.string(),
  rules: z.array(
    z.object({
      field: z.string(),
      op: z.enum(['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'nin', 'contains']),
      value: z.unknown(),
    }),
  ),
  combinator: z.enum(['and', 'or']).default('and'),
  createdAt: TimestampSchema,
});
export type AudienceSegment = z.infer<typeof AudienceSegmentSchema>;

export const CampaignChannelSchema = z.enum(['web_push', 'whatsapp', 'in_app_banner']);
export type CampaignChannel = z.infer<typeof CampaignChannelSchema>;

export const CampaignContentSchema = z.object({
  title: I18nStringSchema,
  body: I18nStringSchema,
  imageUrl: z.string().url().optional(),
  deepLink: z.string().optional(),
  attachedVoucherTemplateId: ShortIdSchema.optional(),
});

export const CampaignStatusSchema = z.enum(['draft', 'scheduled', 'sending', 'sent', 'failed']);
export type CampaignStatus = z.infer<typeof CampaignStatusSchema>;

export const CampaignSchema = z.object({
  id: ShortIdSchema,
  channels: z.array(CampaignChannelSchema).min(1),
  content: CampaignContentSchema,
  audience: z.union([
    z.object({ kind: z.literal('segment'), segmentId: ShortIdSchema }),
    z.object({ kind: z.literal('prebuilt'), key: z.string() }),
    z.object({ kind: z.literal('all') }),
  ]),
  schedule: z.object({
    sendAt: TimestampSchema,
    timezone: z.string().default('Asia/Kuala_Lumpur'),
  }),
  status: CampaignStatusSchema,
  createdBy: UidSchema,
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  stats: z
    .object({
      sent: z.number().int().nonnegative().default(0),
      delivered: z.number().int().nonnegative().default(0),
      opened: z.number().int().nonnegative().default(0),
      clicked: z.number().int().nonnegative().default(0),
      voucherClaimed: z.number().int().nonnegative().default(0),
      booked: z.number().int().nonnegative().default(0),
    })
    .default({ sent: 0, delivered: 0, opened: 0, clicked: 0, voucherClaimed: 0, booked: 0 }),
});
export type Campaign = z.infer<typeof CampaignSchema>;

export const CampaignDeliverySchema = z.object({
  id: ShortIdSchema,
  campaignId: ShortIdSchema,
  customerUid: UidSchema,
  channel: CampaignChannelSchema,
  sentAt: TimestampSchema,
  deliveredAt: TimestampSchema.optional(),
  openedAt: TimestampSchema.optional(),
  clickedAt: TimestampSchema.optional(),
  voucherClaimedAt: TimestampSchema.optional(),
  bookingId: z.string().optional(),
  failure: z
    .object({
      reason: z.string(),
      providerCode: z.string().optional(),
    })
    .optional(),
});
export type CampaignDelivery = z.infer<typeof CampaignDeliverySchema>;
